import { BookmarkSquareIcon, CalendarDaysIcon, ChevronDownIcon, NoSymbolIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon, CursorArrowRaysIcon } from '@heroicons/react/24/solid';
import * as Sentry from '@sentry/react';
import dayjs, { type Dayjs } from 'dayjs';
import React, { useEffect, useMemo, useState } from 'react';
import { Flexor } from 'shared/src/components';
import { ScreenReaderOnly } from 'shared/src/components/Accessibility';
import { SectionSubSubHeading } from 'shared/src/components/SectionHeading';
import { Alert, AlertDescription } from 'shared/src/components/ui/new';
import { cn } from 'shared/src/utils/classNames';
import { toast } from 'sonner';
import { useParams } from 'wouter';
import { getAvailabilitySurveyWorkDates, saveAvailabilitySurveyWorkDates } from '../fetchers';
import { apiDateConverter } from '../utils/apiDateConverter';
import Calendar from './Calendar';
import WorkScheduleResponseMenu from './WorkScheduleResponseMenu';

type WorkDate = {
  DateSurveyed?: string;
  IsAvailable?: boolean;
  UnformattedDate?: string;
};

type WorkDatePayload = {
  LogoPath: string;
  CustomerName: string;
  City: string;
  State: string;
  Zip: string;
  Phone: string;
  ImgPath: string;
  UserName: string;
  ElectionName: string;
  SurveyDates: Dayjs[];
  WorkDates: WorkDate[];
  WelcomeMessage: string;
  BackgroundColor: string;
  TextColor: string;
  VersionNumber?: number;
};

export default function AvailabilitySurvey() {
  const [scheduleData, setScheduleData] = useState<WorkDatePayload>();
  const [workDates, setWorkDates] = useState<WorkDate[]>([]);
  const [startDate, setStartDate] = useState(dayjs());
  const [show404, setShow404] = useState(false);
  const { scheduleSurveyId, evUserId } = useParams();

  useEffect(() => {
    loadWorkDates(scheduleSurveyId, evUserId);
  }, [scheduleSurveyId, evUserId]);

  const isComplete = useMemo(() => !!workDates.filter(({ IsAvailable: isAvailable }) => Boolean(isAvailable)).length, [workDates]);

  async function loadWorkDates(scheduleSurveyId?: string, evUserId?: string) {
    if (!scheduleSurveyId || !evUserId) return;

    return getAvailabilitySurveyWorkDates(scheduleSurveyId, evUserId)
      .then((workDatesResp) => {
        setScheduleData(workDatesResp);

        const workDates = workDatesResp.WorkDates.reduce((acc: Record<string, WorkDate>, date: WorkDate) => {
          const formatted = dayjs.utc(apiDateConverter(date.DateSurveyed)).format('YYYY-MM-DD');
          acc[formatted] = { ...date, DateSurveyed: formatted, UnformattedDate: apiDateConverter(date.DateSurveyed) };
          return acc;
        }, {});

        const { VersionNumber: surveyVersion = 1 } = workDatesResp;

        if (surveyVersion === 2) {
          const sortedWorkDates = sortWorkDates(Object.values(workDates));
          setWorkDates(sortedWorkDates);
          if (sortedWorkDates.length > 0) setStartDate(dayjs(sortedWorkDates[0].DateSurveyed));
          return;
        }

        const workDatesTransformed = workDatesResp.SurveyDates.reduce(
          (acc: Record<string, WorkDate>, date: string) => {
            const formatted = dayjs.utc(apiDateConverter(date)).format('YYYY-MM-DD');
            acc[formatted] = { DateSurveyed: formatted, ...(workDates[formatted] || {}), UnformattedDate: apiDateConverter(date) };
            return acc;
          },
          {} as Record<string, WorkDate>,
        );

        const workDatesTransformedList = Object.values(workDatesTransformed) as WorkDate[];

        setWorkDates(sortWorkDates(workDatesTransformedList));
        setStartDate(dayjs(workDatesTransformedList[0].DateSurveyed));
      })
      .catch((err) => {
        Sentry.captureException(err, { tags: { scheduleSurveyId, evUserId } });
        setShow404(true);
      });
  }

  function sortWorkDates(workDates: WorkDate[]) {
    return workDates.sort((a: WorkDate, b: WorkDate) => {
      if (!a?.DateSurveyed || !b?.DateSurveyed) return 0;
      return dayjs(a.DateSurveyed).diff(dayjs(b.DateSurveyed));
    });
  }

  function responseMenuLabel(isAvailable?: boolean) {
    let indicatorElem = null;

    switch (isAvailable) {
      case true:
        indicatorElem = (
          <>
            <div className="rounded-full h-3 w-3 bg-green-500" />
            <span>Confirmed</span>
          </>
        );
        break;
      case false:
        indicatorElem = (
          <>
            <div className="rounded-full h-3 w-3 bg-red-500" />
            <span>Decline</span>
          </>
        );
        break;
      default:
        indicatorElem = (
          <>
            <div className="rounded-full h-3 w-3 bg-gray-500" />
            <span>No Response</span>
          </>
        );
        break;
    }

    return <Flexor className="space-x-1">{indicatorElem}</Flexor>;
  }

  function updateSchedule(dateSurveyed: string, selection: boolean) {
    if (!scheduleSurveyId || !evUserId) return;

    const savePromise = new Promise((resolve, reject) => {
      saveAvailabilitySurveyWorkDates(scheduleSurveyId, evUserId, dateSurveyed, selection)
        .then((isSaved) => {
          if (!isSaved) return reject();

          loadWorkDates(scheduleSurveyId, evUserId).then(resolve);
        })
        .catch(reject);
    });

    toast.promise(savePromise, { loading: 'Updating availability...', success: 'Saved!', error: 'Could not be saved, try again.' });
  }

  if (show404) {
    return (
      <div className="rounded-lg w-full h-96 border-dashed border-2 flex flex-col items-center justify-center space-y-5">
        <NoSymbolIcon className="h-14 w-14" />
        <h1 className="font-semibold" aria-level={1}>
          The Availability Survey could not be found
        </h1>
        <div className="text-left space-y-3 flex flex-col md:w-1/4 w-5/6 text-sm text-gray-700">
          <h3 className="font-semibold">Possible issues...</h3>
          <ul className="list-disc pl-5">
            <li>You have an incorrect survey link</li>
            <li>The election is no longer active</li>
            <li>Your user could not be found</li>
          </ul>
          <h3 className="font-semibold">Please reach out to get a new link.</h3>
        </div>
      </div>
    );
  }

  return (
    <form className="mt-5" onSubmit={(e) => e.preventDefault()}>
      <Flexor>
        <h1 className="text-base font-semibold leading-6 text-gray-900 mb-4" aria-level={1}>
          {scheduleData?.UserName?.trim() ? `${scheduleData?.UserName}'s ` : ''}Availability Survey
        </h1>
        <div className="flex items-center space-x-1">
          <BookmarkSquareIcon className="h-5 w-5" />
          <span className="font-semibold">{scheduleData?.ElectionName}</span>
        </div>
      </Flexor>
      <div className="lg:flex justify-between items-center">
        <div className="space-y-2 text-sm text-gray-700">
          <div className="flex lg:flex-row flex-row-revedrse items-center lg:space-x-2">
            <span className="hidden lg:inline">Use the</span>
            <strong className="p-1 flex space-x-2 px-2 mx-2 items-center border border-gray-300 shadow-sm rounded-lg">
              <span className="h-4 w-4 rotate-45 rounded-full bg-gradient-to-r from-green-500 via-gray-500 to-70% to-red-500" />
              <span>Response</span>
              <ChevronDownIcon className="h-4 w-4" />
            </strong>
            <span>
              <span className="inline lg:hidden">Use the response</span> button to alert staff which scheduled times you are able to fulfill.
            </span>
          </div>
        </div>
      </div>
      <div className="mt-5">
        {isComplete ? (
          <Alert className="mb-5" variant="success">
            <CheckCircleIcon className="size-5" />
            <AlertDescription>Thank you for your response. Feel free to make any changes you need to.</AlertDescription>
          </Alert>
        ) : (
          <>
            {!scheduleData?.WelcomeMessage ? null : (
              <Alert className="mb-5" variant="information">
                <CursorArrowRaysIcon className="size-5" />
                <AlertDescription>{scheduleData?.WelcomeMessage}</AlertDescription>
              </Alert>
            )}
          </>
        )}
      </div>
      <div className="lg:flex lg:space-y-0 space-y-10 gap-5 mt-5">
        <div className="lg:w-1/2">
          <SectionSubSubHeading>Dates</SectionSubSubHeading>
          <div className="flex flex-col mt-14 justify-center space-y-5">
            {workDates.map(({ DateSurveyed: dateSurveyed, UnformattedDate: unformattedDateSurveyed, IsAvailable: isAvailable }) => {
              if (!dateSurveyed || !unformattedDateSurveyed) return null;

              return (
                <div
                  key={dateSurveyed}
                  className={cn(dayjs(startDate).isSame(dateSurveyed) ? 'ring-2 ring-primary' : '', 'border rounded-lg p-4 flex justify-between items-center font-semibold')}
                  onClick={() => setStartDate(dayjs(dateSurveyed))}
                >
                  <Flexor className="space-x-2">
                    <CalendarDaysIcon className="h-5 w-5" />
                    <div>{dayjs(dateSurveyed).format('MMMM DD, YYYY')}</div>
                  </Flexor>
                  <div>
                    <WorkScheduleResponseMenu label={responseMenuLabel(isAvailable)} onSelect={(selection) => updateSchedule(unformattedDateSurveyed, selection)} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="lg:w-1/2">
          <ScreenReaderOnly>Visual representation of availability in a calendar view</ScreenReaderOnly>
          <div className="sticky top-5">
            <Calendar startDay={startDate} onChange={setStartDate} aria-hidden="true" />
          </div>
        </div>
      </div>
    </form>
  );
}
